import type { IntlShape } from 'react-intl';

import type { CreditAllowance, WorkspaceMemberCreditAllowance } from '../../contracts';

/** Highest crossed usage percent that should show a nudge. 100 is remaining <= 0. */
export const CREDIT_ALLOWANCE_NUDGE_THRESHOLDS = [50, 75, 90, 100] as const;

export type CreditAllowanceNudgeVariant =
  (typeof CREDIT_ALLOWANCE_NUDGE_THRESHOLDS)[number];

export const CREDIT_AMOUNT_DISPLAY_MAX_FRACTION_DIGITS = 1;

function parseCreditAmount(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Floors consumed/remaining API strings to one decimal — never overstates spend. */
export function truncateCreditAmountForDisplay(value: string): number {
  const parsed = parseCreditAmount(value);
  const factor = 10 ** CREDIT_AMOUNT_DISPLAY_MAX_FRACTION_DIGITS;
  return Math.floor(parsed * factor) / factor;
}

export type CreditAllowanceDisplayMetrics = {
  consumed: number;
  limit: number;
  usagePercent: number;
};

/**
 * Display-facing credit usage. Consumed is floored to one decimal; the returned
 * limit is the exact plan display limit. Progress derives from those values.
 */
export function getCreditAllowanceDisplayMetrics(
  allowance: CreditAllowance
): CreditAllowanceDisplayMetrics {
  const consumed = truncateCreditAmountForDisplay(allowance.consumed);
  const limit = parseCreditAmount(allowance.display_limit);
  if (limit <= 0) {
    return { consumed, limit, usagePercent: 0 };
  }

  return {
    consumed,
    limit,
    usagePercent: clampCreditUsagePercent(Math.floor((consumed / limit) * 100)),
  };
}

/** Locale-aware credit amount for UI copy. */
export function formatCreditAmountForDisplay(
  intl: IntlShape,
  value: string,
  kind: 'consumed' | 'remaining' | 'limit'
): string {
  if (kind === 'limit') {
    const parsed = parseCreditAmount(value);
    return intl.formatNumber(parsed, {
      minimumFractionDigits: 0,
      maximumFractionDigits: Number.isInteger(parsed)
        ? 0
        : CREDIT_AMOUNT_DISPLAY_MAX_FRACTION_DIGITS,
    });
  }

  return intl.formatNumber(truncateCreditAmountForDisplay(value), {
    minimumFractionDigits: 0,
    maximumFractionDigits: CREDIT_AMOUNT_DISPLAY_MAX_FRACTION_DIGITS,
  });
}

function getCreditAllowanceRawUsagePercent(allowance: CreditAllowance): number {
  const limit = parseCreditAmount(allowance.limit);
  if (limit <= 0) {
    return 0;
  }

  return clampCreditUsagePercent((parseCreditAmount(allowance.consumed) / limit) * 100);
}

/**
 * Effective `limit` above which a cycle is uncapped in practice. The backend sends
 * 99999999 today, but any grant-scale pool means the same thing, so this is a
 * threshold rather than a sentinel match.
 */
export const COMPLIMENTARY_CREDIT_ALLOWANCE_LIMIT_MIN = 99_999;

/**
 * True when this cycle's effective limit is uncapped. Complimentary copy keys off
 * the cycle, not the cohort flag — a flagged user whose cycle is capped still pays.
 * Progress stays on `display_limit`.
 */
export function isComplimentaryCreditAllowance(
  allowance: CreditAllowance | null | undefined
): boolean {
  return (
    allowance != null &&
    parseCreditAmount(allowance.limit) > COMPLIMENTARY_CREDIT_ALLOWANCE_LIMIT_MIN
  );
}

export type WorkspaceTeamUsage = {
  consumed: number;
  limit: number;
  /** `consumed / limit`; `null` when no member has a plan allowance to measure against. */
  ratio: number | null;
};

/**
 * Sums every member's current cycle against their plan allowance
 * (`display_limit`). The effective `limit` is skipped because a complimentary
 * grant inflates it far past what the team's seats actually include.
 */
export function toWorkspaceTeamUsage(
  members: readonly WorkspaceMemberCreditAllowance[]
): WorkspaceTeamUsage {
  let consumed = 0;
  let limit = 0;
  for (const { allowance } of members) {
    if (!allowance) continue;
    const memberLimit = parseCreditAmount(allowance.display_limit);
    if (memberLimit <= 0) continue;
    consumed += parseCreditAmount(allowance.consumed);
    limit += memberLimit;
  }
  return { consumed, limit, ratio: limit > 0 ? consumed / limit : null };
}

/** Team usage share of its plan allowance above which the Max seat is recommended. */
export const MAX_SEAT_RECOMMENDED_USAGE_RATIO = 0.3;

export function isMaxSeatRecommended(ratio: number | null | undefined): boolean {
  return ratio != null && ratio > MAX_SEAT_RECOMMENDED_USAGE_RATIO;
}

/** Clamps a whole-number usage percent to 0–100 for progress UI. */
export function clampCreditUsagePercent(usagePercent: number): number {
  return Math.min(100, Math.max(0, usagePercent));
}

/** Floored display usage percent (0–100), aligned with truncated credit amounts. */
export function getCreditAllowanceUsagePercent(allowance: CreditAllowance): number {
  return getCreditAllowanceDisplayMetrics(allowance).usagePercent;
}

/**
 * Highest nudge threshold the current allowance has crossed, if any.
 * `null` means no nudge — including loading, missing, and stale-unavailable
 * bodies, which must not flash a false 50/75/90/100 banner.
 *
 * 100 is effective remaining <= 0 (exact). 50 / 75 / 90 use the raw effective
 * limit ratio; display metrics separately use the plan display limit.
 */
export function getCreditAllowanceNudgeVariant(
  allowance: CreditAllowance | null | undefined
): CreditAllowanceNudgeVariant | null {
  if (allowance == null || parseCreditAmount(allowance.limit) <= 0) {
    return null;
  }
  if (parseCreditAmount(allowance.remaining) <= 0) {
    return 100;
  }

  const percent = getCreditAllowanceRawUsagePercent(allowance);
  if (percent >= 90) {
    return 90;
  }
  if (percent >= 75) {
    return 75;
  }
  if (percent >= 50) {
    return 50;
  }
  return null;
}
